import React, { useState, useMemo, useCallback } from 'react';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import Box from '../../components/Common/Box';
import Loader from '../../components/Common/Loader';
import { useNavigate } from 'react-router-dom';
import { useValuationRanking, useCompleteValuation, ValuationItem, CompleteValuation } from '../../hooks/useValuationRanking';
import { useScreenType } from '../../hooks/useScreenType';

type SortColumn = 'symbole' | 'cours_actuel' | 'position_52s' | 'prix_moyen' | 'potentiel_pct' | 'recommendation' | 'fiabilite' | 'graham_number' | 'dcf' | 'ev_revenue' | 'ps' | 'peg_score';
type SortDirection = 'asc' | 'desc';

/** Colonnes « prix d'un modèle » : triées sur l'écart au cours, pas sur le prix brut. */
const MODEL_PRICE_COLUMNS = ['graham_number', 'dcf', 'ev_revenue', 'ps', 'prix_moyen'] as const;
type ModelPriceColumn = typeof MODEL_PRICE_COLUMNS[number];

const FIABILITE_RANK: Record<string, number> = {
    'ÉLEVÉE': 4, 'ELEVEE': 4, 'MOYENNE': 3, 'SPÉCULATIVE': 2, 'SPECULATIVE': 2, 'FAIBLE': 1,
};

/* Libellés partagés par les listes déroulantes et les puces de filtres actifs */
const LYNCH_LABELS: Record<string, string> = {
    FAST_GROWER: '🚀 Forte Croissance',
    STALWART: '🏛️ Pilier / Stalwart',
    CYCLICAL: '🔄 Valeur Cyclique',
    ASSET_PLAY: "💎 Jeu d'Actifs",
    TURNAROUND: '🔧 Redressement',
    SLOW_GROWER: '🐢 Croissance Lente',
    UNCLASSIFIED: '❔ Non Classée',
};
/* Statut renvoyé par valuation.py : écart entre le prix pondéré et le cours, seuil ±20 %.
   Libellés descriptifs : Kaneabourse ne délivre pas de signal d'achat ou de vente. */
const STATUT_LABELS: Record<string, string> = {
    'SOUS-ÉVALUÉE': 'Décote > 20 %',
    'CORRECTEMENT ÉVALUÉE': 'Écart < 20 %',
    'SUR-ÉVALUÉE': 'Prime > 20 %',
};
const statutLabel = (rec: string): string => STATUT_LABELS[rec] ?? 'Indéterminé';
const PEG_LABELS: Record<string, string> = {
    very_attractive: 'Score ≥ 2',
    good: 'Score 1,5 – 2',
    expensive: 'Score < 1',
};
/* Composantes de `ttm_method` (valuation.py::_build_smart_ttm), jointes par « / » quand les métriques
   d'une même valeur n'ont pas été annualisées de la même façon (ex. « TTM_FORMULA/ANNUAL_FALLBACK »).
   Les anciennes clés ANNUAL / S1_ANNUALIZED / Q3_ANNUALIZED n'existaient pas : le filtre ne trouvait rien. */
const TTM_LABELS: Record<string, string> = {
    ANNUAL_DIRECT: 'Annuel publié',
    TTM_FORMULA: '12 mois glissants',
    COMBINATION: 'Combinaison de périodes',
    COEFFICIENT: 'Annualisé par coefficient',
    ANNUAL_FALLBACK: 'Repli sur le dernier annuel',
};
const TTM_COURTS: Record<string, string> = {
    ANNUAL_DIRECT: 'Annuel', TTM_FORMULA: '12 m gliss.', COMBINATION: 'Combinaison',
    COEFFICIENT: 'Coef.', ANNUAL_FALLBACK: 'Repli annuel',
};
const composantesTtm = (m: string | null | undefined): string[] => (m ? m.split('/').filter(Boolean) : []);
const libelleTtm = (m: string | null | undefined, court = false): string => {
    const c = composantesTtm(m);
    if (!c.length) return '—';
    return c.map(x => (court ? TTM_COURTS : TTM_LABELS)[x] ?? x).join(court ? ' + ' : ', ');
};

/** Vues rapides : chaque identifiant est un préréglage de `applyPreset`. */
const QUICK_VIEWS: { id: string; label: string; color: string }[] = [
    { id: 'evrev_pe', label: '📊 EV/Rev + P/E', color: '#0d9488' },
    { id: 'triple_multiples', label: '🎯 Triple Multiples', color: '#2563eb' },
    { id: 'fast_grower', label: '🚀 Forte Croissance', color: '#7c3aed' },
    { id: 'undervalued', label: 'Décote > 20 %', color: '#16a34a' },
    { id: 'complete_data', label: '💎 Fiabilité élevée', color: '#0f766e' },
];

/** Écart en % entre le prix d'un modèle et le cours ; null si l'un des deux manque. */
const ecartPct = (prix: number | null | undefined, cours: number | null | undefined): number | null =>
    prix && cours ? ((prix - cours) / cours) * 100 : null;

/* ─────────────────────────────────────────────
   Cellule « prix d'un modèle + écart au cours »
───────────────────────────────────────────── */
const ModelPriceCell: React.FC<{
    price: number | null | undefined;
    cours: number | null;
    priceColor?: string;
    emphasis?: boolean;
}> = ({ price, cours, priceColor, emphasis = false }) => {
    const pct = ecartPct(price, cours);
    return (
        <td style={{ textAlign: 'center', padding: '12px 10px', fontSize: '12px', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
            {price ? (
                <>
                    <div style={{ fontWeight: emphasis ? 700 : 600, fontSize: emphasis ? '13px' : '12px', color: priceColor }}>
                        {price.toLocaleString('fr-FR')} F
                    </div>
                    {pct !== null && (
                        <div style={{ fontSize: '11px', fontWeight: emphasis ? 700 : 400, color: pct > 0 ? '#16a34a' : '#dc2626' }}>
                            {pct > 0 ? '+' : ''}{pct.toFixed(1)}%
                        </div>
                    )}
                </>
            ) : <span style={{ color: '#cbd5e1' }}>—</span>}
        </td>
    );
};

/* ─────────────────────────────────────────────
   Price Gauge Component
───────────────────────────────────────────── */
const PriceGauge: React.FC<{
    min: number | null;
    max: number | null;
    current: number | null;
    target: number | null;
    targetColor?: string;
}> = ({ min, max, current, target, targetColor = '#7c3aed' }) => {
    if (!min || !max || !current || min >= max) return null;
    const clamp = (v: number) => Math.max(0, Math.min(100, ((v - min) / (max - min)) * 100));
    const currentPct = clamp(current);
    const targetPct = target ? clamp(target) : null;
    return (
        <div style={{ marginTop: '6px', position: 'relative', height: '6px', borderRadius: '3px', background: 'linear-gradient(to right, #fee2e2, #fef9c3, #dcfce7)', overflow: 'visible' }}>
            {/* Current price marker */}
            <div style={{
                position: 'absolute', left: `${currentPct}%`, top: '-4px',
                width: '14px', height: '14px', borderRadius: '50%',
                background: '#1e40af', border: '2px solid #fff',
                transform: 'translateX(-50%)',
                boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                zIndex: 2,
            }} />
            {/* Target price marker */}
            {targetPct !== null && (
                <div style={{
                    position: 'absolute', left: `${targetPct}%`, top: '-3px',
                    width: '12px', height: '12px', borderRadius: '2px',
                    background: targetColor, border: '2px solid #fff',
                    transform: 'translateX(-50%) rotate(45deg)',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                    zIndex: 1,
                }} />
            )}
        </div>
    );
};

/* ─────────────────────────────────────────────
   Fenêtre de détail
───────────────────────────────────────────── */
/** Les 15 modèles de la synthèse (valuation.py::calculate_complete_valuation), RIM compris.
 *  `category` = catégorie de pondération du backend. */
const MODEL_META: { key: string; label: string; emoji: string; category: string; categorieApi: 'dividendes' | 'multiples' | 'intrinseque' | 'theorique' }[] = [
    { key: 'yield_reversion',   label: 'Yield Reversion',    emoji: '📈', category: 'Dividendes' , categorieApi: 'dividendes' },
    { key: 'gordon_shapiro',    label: 'Gordon-Shapiro',      emoji: '📐', category: 'Dividendes' , categorieApi: 'dividendes' },
    { key: 'chowder_projection',label: 'Chowder (1 an)',      emoji: '🎯', category: 'Dividendes' , categorieApi: 'dividendes' },
    { key: 'capm_projection',   label: 'CAPM',                emoji: '⚡', category: 'Théoriques' , categorieApi: 'theorique' },
    { key: 'graham_simplified', label: 'Graham Simplifié',    emoji: '📖', category: 'Théoriques' , categorieApi: 'theorique' },
    { key: 'pe_model',          label: 'P/E',                 emoji: '💹', category: 'Multiples' , categorieApi: 'multiples' },
    { key: 'ev_ebitda_model',   label: 'EV/EBITDA',           emoji: '🏦', category: 'Multiples' , categorieApi: 'multiples' },
    { key: 'ev_ebit_model',     label: 'EV/EBIT',             emoji: '🏛️', category: 'Multiples' , categorieApi: 'multiples' },
    { key: 'ps_model',          label: 'P/S',                 emoji: '💰', category: 'Multiples' , categorieApi: 'multiples' },
    { key: 'ev_revenue_model',  label: 'EV/Revenue',          emoji: '📊', category: 'Multiples' , categorieApi: 'multiples' },
    { key: 'pb_model',          label: 'P/Book',              emoji: '📚', category: 'Multiples' , categorieApi: 'multiples' },
    { key: 'rim_model',         label: 'RIM (Ohlson)',        emoji: '🏦', category: 'Intrinsèques' , categorieApi: 'intrinseque' },
    { key: 'graham_number',     label: 'Graham Number',       emoji: '🔢', category: 'Intrinsèques' , categorieApi: 'intrinseque' },
    { key: 'epv_model',         label: 'EPV',                 emoji: '⚖️', category: 'Intrinsèques' , categorieApi: 'intrinseque' },
    { key: 'dcf_model',         label: 'DCF',                 emoji: '🔮', category: 'Intrinsèques' , categorieApi: 'intrinseque' },
];
const NB_MODELES = MODEL_META.length;

/* ─────────────────────────────────────────────
   Signaux de convergence (descriptifs, calculés côté écran)
───────────────────────────────────────────── */
type OpportunitySignal = {
    id: string;
    label: string;
    emoji: string;
    color: string;
    bg: string;
    tooltip: string;
};

/** Définitions des signaux (utilisées à la fois pour le calcul et les checkboxes UI) */
const ALL_SIGNAL_DEFS: { id: string; label: string; emoji: string; color: string; bg: string; desc: string }[] = [
    { id: 'triple_conviction', label: 'Convergence Graham / DCF / IVK',    emoji: '🔥', color: '#7c3aed', bg: '#ede9fe',
      desc: 'Graham Number, DCF et prix pondéré tous au moins 10 % au-dessus du cours' },
    { id: 'triple_multiples',  label: 'Triple Multiples (P/E / P/S / EV/Rev)', emoji: '🎯', color: '#2563eb', bg: '#eff6ff',
      desc: 'P/E, P/S et EV/Revenue tous au moins 10 % au-dessus du cours' },
    { id: 'ev_rev_pe',         label: 'EV/Revenue + P/E', emoji: '📊', color: '#0d9488', bg: '#ccfbf1',
      desc: "EV/Revenue et P/E tous deux au moins 20 % au-dessus du cours. Critère de la watchlist Value ; son backtest n'est pas encore validé." },
    { id: 'breakout_fond',     label: 'Entre prix moyen et pondéré', emoji: '⚡', color: '#ea580c', bg: '#fff7ed',
      desc: 'Cours au-dessus du prix moyen des modèles, encore au moins 5 % sous le prix pondéré' },
    { id: 'palier_imminent',   label: 'Modèle proche',      emoji: '📍', color: '#0284c7', bg: '#e0f2fe',
      desc: "Le prix d'un modèle de référence est de 0 à 15 % au-dessus du cours" },
    { id: 'haute_conviction',  label: 'Décote à fiabilité élevée',     emoji: '💎', color: '#0f766e', bg: '#ccfbf1',
      desc: 'Fiabilité élevée, au moins 10 modèles calculés et prix pondéré au moins 20 % au-dessus du cours' },
];

function computeOpportunitySignals(item: ValuationItem): OpportunitySignal[] {
    const signals: OpportunitySignal[] = [];
    const cours = item.cours_actuel;
    if (!cours || cours <= 0) return signals;

    const upPct = (v: number | null | undefined) =>
        v != null && v > 0 ? (v - cours) / cours * 100 : null;

    const grahamPct = upPct(item.graham_number);
    const dcfPct    = upPct(item.dcf);
    const ponderPct = item.potentiel_pct;

    // 🔥 Triple Conviction : les 3 familles convergent > +10%
    if (grahamPct !== null && grahamPct > 10
        && dcfPct !== null && dcfPct > 10
        && ponderPct !== null && ponderPct > 10) {
        signals.push({
            id: 'triple_conviction', label: 'Convergence Graham/DCF/IVK', emoji: '🔥',
            color: '#7c3aed', bg: '#ede9fe',
            tooltip: `Graham +${grahamPct.toFixed(0)}%, DCF +${dcfPct.toFixed(0)}%, Pondéré +${ponderPct.toFixed(0)}%`,
        });
    }

    // 🎯 Triple Multiples : P/E + P/S + EV/Revenue tous > +10%
    const pePct    = upPct(item.pe);
    const psPct    = upPct(item.ps);
    const evRevPct = upPct(item.ev_revenue);

    if (pePct !== null && pePct > 10
        && psPct !== null && psPct > 10
        && evRevPct !== null && evRevPct > 10) {
        signals.push({
            id: 'triple_multiples', label: 'Triple Multiples', emoji: '🎯',
            color: '#2563eb', bg: '#eff6ff',
            tooltip: `P/E +${pePct.toFixed(0)}%, P/S +${psPct.toFixed(0)}%, EV/Rev +${evRevPct.toFixed(0)}%`,
        });
    }

    // 📊 Value BRVM : EV/Revenue ET P/E tous deux > +20% (stratégie 2 facteurs du backtest)
    if (pePct !== null && pePct > 20 && evRevPct !== null && evRevPct > 20) {
        signals.push({
            id: 'ev_rev_pe', label: 'EV/Rev + P/E', emoji: '📊',
            color: '#0d9488', bg: '#ccfbf1',
            tooltip: `EV/Revenue +${evRevPct.toFixed(0)}% et P/E +${pePct.toFixed(0)}% au-dessus du cours`,
        });
    }

    // ⚡ Breakout fondamental : cours > prix_moyen, encore sous prix_pondéré
    if (item.prix_moyen && cours > item.prix_moyen
        && item.prix_pondere && cours < item.prix_pondere
        && ponderPct !== null && ponderPct >= 5) {
        const gained = ((cours - item.prix_moyen) / item.prix_moyen * 100).toFixed(0);
        signals.push({
            id: 'breakout_fond', label: 'Entre moyen et pondéré', emoji: '⚡',
            color: '#ea580c', bg: '#fff7ed',
            tooltip: `Cours ${gained}% au-dessus du prix moyen, ${ponderPct.toFixed(0)}% sous le prix pondéré`,
        });
    }

    // 📍 Palier imminent : cours à 0–15% sous un modèle spécifique
    // Liste contextuelle selon le secteur :
    // - Financier (banques, assurances) : EV/EBITDA EXCLUE (la dette est leur cœur de métier,
    //   pas un coût à retraiter ; les métriques EV/EV-based n'ont pas de sens pour eux).
    //   Modèles retenus : P/E, P/Sales (= P/Rev. net d'intérêts), P/Book, Graham, DCF, Yield Rev.
    // - Autres secteurs : P/E, P/Sales, P/Book, EV/EBITDA, Graham, DCF, Yield Rev.
    const isFinancial = item.secteur != null && item.secteur.toLowerCase().includes('financier');
    const namedModels = [
        { label: 'P/E',        price: item.pe },             // ancre universelle, la plus suivie
        { label: 'P/Sales',    price: item.ps },             // clé pour banques (ex: CBIBF) et revenus
        { label: 'P/Book',     price: item.pb },             // ancre naturelle secteur financier
        ...(isFinancial ? [{ label: 'RIM', price: item.rim }] : []), // modèle de référence secteur financier
        ...(!isFinancial ? [{ label: 'EV/EBITDA', price: item.ev_ebitda }] : []), // non applicable aux banques
        { label: 'Graham',     price: item.graham_number },  // valeur intrinsèque Benjamin Graham
        { label: 'DCF',        price: item.dcf },            // plafond de valeur fondamentale
        { label: 'Yield Rev.', price: item.yield_reversion },// équilibre dividendes (SNTS, SLBC…)
    ];
    const nearestAbove = namedModels
        .map(m => ({ ...m, up: upPct(m.price) }))
        .filter(m => m.up !== null && m.up > 0 && m.up <= 15)
        .sort((a, b) => a.up! - b.up!)[0];

    if (nearestAbove) {
        signals.push({
            id: 'palier_imminent', label: `Proche ${nearestAbove.label}`, emoji: '📍',
            color: '#0284c7', bg: '#e0f2fe',
            tooltip: `Prix du modèle ${nearestAbove.label} ${nearestAbove.up!.toFixed(1)}% au-dessus du cours`,
        });
    }

    // 💎 Haute Conviction : toutes les garanties qualitatives réunies
    if ((item.fiabilite === 'ÉLEVÉE' || item.fiabilite === 'ELEVEE')
        && item.nb_modeles >= 10
        && item.recommendation === 'SOUS-ÉVALUÉE'
        && ponderPct !== null && ponderPct >= 20) {
        signals.push({
            id: 'haute_conviction', label: 'Décote, fiabilité élevée', emoji: '💎',
            color: '#0f766e', bg: '#ccfbf1',
            tooltip: `Fiabilité élevée, ${item.nb_modeles}/${NB_MODELES} modèles, prix pondéré +${ponderPct.toFixed(0)}% au-dessus du cours`,
        });
    }

    return signals;
}

const DrillDownModal: React.FC<{ item: ValuationItem; selectedDate?: string; onClose: () => void; compact?: boolean }> = ({ item, selectedDate, onClose, compact = false }) => {
    const navigate = useNavigate();
    const { detail, loading: modalLoading, error: modalError } = useCompleteValuation(item.symbole, selectedDate);

    const formatP = (v: number | null | undefined) => {
        if (v == null) return '—';
        return v.toLocaleString('fr-FR') + ' F';
    };
    const pctVsCurrent = (v: number | null | undefined) => {
        if (v == null || !item.cours_actuel) return null;
        return ((v - item.cours_actuel) / item.cours_actuel) * 100;
    };

    const getModelPrice = (key: string): number | undefined => {
        if (!detail) return undefined;
        if (key === 'yield_reversion') return detail.yield_reversion?.prix_juste;
        if (key === 'gordon_shapiro') return detail.gordon_shapiro?.prix_juste;
        if (key === 'chowder_projection') return detail.chowder_projection?.prix_cible;
        if (key === 'capm_projection') return detail.capm_projection?.prix_cible;
        if (key === 'graham_simplified') return detail.graham_simplified?.prix_juste;
        if (key === 'dcf_model') return detail.dcf_model?.prix_juste;
        const mm = detail.market_multiples;
        if (!mm) return undefined;
        if (key === 'pe_model') return mm.pe_model?.prix_juste;
        if (key === 'ev_ebitda_model') return mm.ev_ebitda_model?.prix_juste;
        if (key === 'ev_ebit_model') return mm.ev_ebit_model?.prix_juste;
        if (key === 'ps_model') return mm.ps_model?.prix_juste;
        if (key === 'ev_revenue_model') return mm.ev_revenue_model?.prix_juste;
        if (key === 'pb_model') return mm.pb_model?.prix_juste;
        if (key === 'rim_model') return mm.rim_model?.prix_juste;
        if (key === 'graham_number') return mm.graham_number?.prix_juste;
        if (key === 'epv_model') return mm.epv_model?.prix_juste;
        return undefined;
    };

    const categories = [...new Set(MODEL_META.map(m => m.category))];
    const recoStyle = item.recommendation === 'SOUS-ÉVALUÉE'
        ? { bg: 'linear-gradient(135deg, #16a34a, #15803d)', text: statutLabel(item.recommendation) }
        : item.recommendation === 'SUR-ÉVALUÉE'
        ? { bg: 'linear-gradient(135deg, #dc2626, #b91c1c)', text: statutLabel(item.recommendation) }
        : { bg: 'linear-gradient(135deg, #d97706, #b45309)', text: statutLabel(item.recommendation) };

    /* Poids dans le prix pondéré, comme valuation.py : part de la catégorie (renormalisée sur les
       catégories présentes) répartie à parts égales entre ses modèles calculés. */
    const methodo = detail?.synthesis?.methodology;
    const partCategorie = (cat: string): number | null => {
        if (!methodo) return null;
        const pct = (methodo as Record<string, unknown>)[`${cat}_pct`];
        return typeof pct === 'number' ? pct : null;
    };
    const poidsModele = (cat: string): number | null => {
        const pct = partCategorie(cat);
        const n = methodo?.nb_modeles_par_categorie?.[cat];
        return pct != null && n ? pct / n : null;
    };

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed', inset: 0,
                background: 'rgba(15,23,42,0.7)',
                backdropFilter: 'blur(4px)',
                zIndex: 1000,
                display: 'flex', alignItems: compact ? 'flex-end' : 'center', justifyContent: 'center',
                padding: compact ? '0' : '20px',
                animation: 'fadeIn 0.2s ease'
            }}
        >
            <div
                onClick={e => e.stopPropagation()}
                style={{
                    background: 'var(--bg-box, #ffffff)',
                    borderRadius: compact ? '20px 20px 0 0' : '20px',
                    padding: '0',
                    width: '100%',
                    maxWidth: '760px',
                    maxHeight: compact ? '92vh' : '88vh',
                    overflowY: 'auto',
                    boxShadow: '0 25px 60px rgba(0,0,0,0.35)',
                    border: '1px solid rgba(255,255,255,0.15)',
                    animation: 'slideUp 0.25s ease'
                }}
            >
                {/* Header */}
                <div style={{
                    background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)',
                    padding: compact ? '18px 16px' : '24px 28px',
                    borderRadius: '20px 20px 0 0',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'
                }}>
                    <div>
                        <div style={{ fontSize: '13px', color: '#94a3b8', marginBottom: '4px', letterSpacing: '0.05em' }}>
                            VALORISATION DÉTAILLÉE · {NB_MODELES} MODÈLES
                        </div>
                        <div style={{ fontSize: '24px', fontWeight: 700, color: '#fff' }}>{item.symbole}</div>
                        <div style={{ fontSize: '13px', color: '#cbd5e1', marginTop: '2px' }}>{item.nom_societe}</div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
                        <button
                            onClick={onClose}
                            style={{
                                background: 'rgba(255,255,255,0.12)', border: 'none', color: '#fff',
                                width: '36px', height: '36px', borderRadius: '50%',
                                cursor: 'pointer', fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}
                        >✕</button>
                        <span style={{
                            background: recoStyle.bg, color: '#fff',
                            padding: '6px 14px', borderRadius: '20px',
                            fontSize: '12px', fontWeight: 700, whiteSpace: 'nowrap'
                        }}>{recoStyle.text}</span>
                    </div>
                </div>

                <div style={{ padding: compact ? '16px' : '24px 28px' }}>
                    {/* KPI strip */}
                    <div style={{ display: 'grid', gridTemplateColumns: compact ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: compact ? '8px' : '12px', marginBottom: compact ? '16px' : '24px' }}>
                        {[
                            { label: 'Cours Actuel', value: item.cours_actuel, color: '#1e40af' },
                            { label: 'Prix Pondéré', value: item.prix_pondere, color: '#7c3aed' },
                            { label: 'Écart au cours', value: item.potentiel_pct != null ? `${item.potentiel_pct > 0 ? '+' : ''}${item.potentiel_pct?.toFixed(1)}%` : '—', color: (item.potentiel_pct ?? 0) > 0 ? '#16a34a' : '#dc2626', isText: true },
                            { label: 'Fiabilité', value: item.fiabilite, color: item.fiabilite === 'ÉLEVÉE' ? '#16a34a' : item.fiabilite === 'MOYENNE' ? '#d97706' : '#dc2626', isText: true },
                        ].map(kpi => (
                            <div key={kpi.label} style={{
                                background: 'rgba(0,0,0,0.03)',
                                border: '1px solid #e5e7eb',
                                borderRadius: '12px', padding: '14px 12px',
                                textAlign: 'center'
                            }}>
                                <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>{kpi.label}</div>
                                <div style={{ fontSize: '16px', fontWeight: 700, color: kpi.color }}>
                                    {kpi.isText ? kpi.value : (kpi.value != null ? (kpi.value as number).toLocaleString('fr-FR') + ' F' : '—')}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Lynch Profile Badge if present */}
                    {item.lynch_categorie && (
                        <div style={{
                            background: 'rgba(124, 58, 237, 0.06)',
                            border: '1px solid rgba(124, 58, 237, 0.2)',
                            borderRadius: '12px',
                            padding: '14px 18px',
                            marginBottom: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '14px',
                        }}>
                            <span style={{ fontSize: '28px' }}>{item.lynch_emoji}</span>
                            <div>
                                <div style={{ fontSize: '11px', fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                    Classification Peter Lynch
                                </div>
                                <div style={{ fontSize: '16px', fontWeight: 700, color: '#111827', marginTop: '2px' }}>
                                    {item.lynch_titre}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Price gauge */}
                    {item.prix_min_1an && item.prix_max_1an && item.cours_actuel && (
                        <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px', marginBottom: '24px', border: '1px solid #e5e7eb' }}>
                            <div style={{ fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '12px' }}>
                                📍 Position du cours — Fourchette 52 semaines
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
                                <span>Min {item.prix_min_1an.toLocaleString('fr-FR')} F</span>
                                <span>Cours {item.cours_actuel.toLocaleString('fr-FR')} F</span>
                                <span>Max {item.prix_max_1an.toLocaleString('fr-FR')} F</span>
                            </div>
                            <PriceGauge min={item.prix_min_1an} max={item.prix_max_1an} current={item.cours_actuel} target={item.prix_pondere} />
                            <div style={{ display: 'flex', gap: '16px', marginTop: '10px', fontSize: '10px', color: '#6b7280' }}>
                                <span>● Cours actuel</span>
                                <span>◆ Prix pondéré</span>
                            </div>
                        </div>
                    )}

                    {/* Models breakdown */}
                    {modalLoading ? (
                        <div style={{ textAlign: 'center', padding: '30px', color: '#6b7280' }}>
                            <div style={{ fontSize: '24px', marginBottom: '8px' }}>⏳</div>
                            Chargement des modèles…
                        </div>
                    ) : modalError ? (
                        <div style={{ textAlign: 'center', padding: '20px', color: '#dc2626', background: '#fef2f2', borderRadius: '10px' }}>
                            {modalError}
                        </div>
                    ) : (
                        <div>
                            <div style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
                                {MODEL_META.filter(m => getModelPrice(m.key) != null).length} modèles calculés sur {NB_MODELES}
                            </div>
                            <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '14px' }}>
                                Prix pondéré : dividendes 40 %, multiples 35 %, intrinsèques 15 %, théoriques 10 %, renormalisés sur les
                                catégories calculées puis répartis à parts égales entre les modèles de chaque catégorie.
                            </div>
                            {categories.map(cat => {
                                const catModels = MODEL_META.filter(m => m.category === cat && getModelPrice(m.key) != null);
                                if (catModels.length === 0) return null;
                                const part = partCategorie(catModels[0].categorieApi);
                                return (
                                    <div key={cat} style={{ marginBottom: '16px' }}>
                                        <div style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.06em', marginBottom: '8px', textTransform: 'uppercase' }}>
                                            {cat}{part != null && ` · ${part.toFixed(0)} % du prix pondéré`}
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: compact ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(200px, 1fr))', gap: '8px' }}>
                                            {catModels.map(m => {
                                                const price = getModelPrice(m.key)!;
                                                const pct = pctVsCurrent(price);
                                                const isPos = pct != null && pct > 0;
                                                const poids = poidsModele(m.categorieApi);
                                                return (
                                                    <div key={m.key} style={{
                                                        background: pct != null ? (isPos ? 'rgba(22,163,74,0.06)' : 'rgba(220,38,38,0.06)') : '#f8fafc',
                                                        border: `1px solid ${pct != null ? (isPos ? 'rgba(22,163,74,0.2)' : 'rgba(220,38,38,0.2)') : '#e5e7eb'}`,
                                                        borderRadius: '10px',
                                                        padding: '12px',
                                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                                                    }}>
                                                        <div>
                                                            <div style={{ fontSize: '11px', color: '#6b7280' }}>{m.emoji} {m.label}</div>
                                                            <div style={{ fontSize: '14px', fontWeight: 700, color: '#111827', marginTop: '2px' }}>{formatP(price)}</div>
                                                            {poids != null && (
                                                                <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '2px' }}>poids {poids.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %</div>
                                                            )}
                                                        </div>
                                                        {pct != null && (
                                                            <div style={{
                                                                fontSize: '13px', fontWeight: 700,
                                                                color: isPos ? '#16a34a' : '#dc2626',
                                                                background: isPos ? 'rgba(22,163,74,0.12)' : 'rgba(220,38,38,0.12)',
                                                                padding: '4px 8px', borderRadius: '6px'
                                                            }}>
                                                                {pct > 0 ? '+' : ''}{pct.toFixed(1)}%
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <button
                        type="button"
                        onClick={() => navigate(`/data?symbol=${item.symbole}`)}
                        style={{
                            marginTop: '8px', width: '100%', padding: '11px', borderRadius: '10px',
                            border: '1px solid #c7d2fe', background: 'rgba(99,102,241,0.06)', color: '#4338ca',
                            fontSize: '13px', fontWeight: 700, cursor: 'pointer',
                        }}
                    >
                        Voir la fiche société →
                    </button>
                </div>
            </div>
        </div>
    );
};

/* ─────────────────────────────────────────────
   Main Screen
───────────────────────────────────────────── */
const SpecialVraiPrixScreen: React.FC = () => {
    const { isMobile } = useScreenType();
    const [selectedDate, setSelectedDate] = useState<string>('latest');
    const { data, availableDates, loading, error } = useValuationRanking(selectedDate);
    const [selectedItem, setSelectedItem] = useState<ValuationItem | null>(null);
    const [filtersOpen, setFiltersOpen] = useState(false);

    // Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [sectorFilter, setSectorFilter] = useState('all');
    const [countryFilter, setCountryFilter] = useState('all');
    const [recommendationFilter, setRecommendationFilter] = useState('all');
    const [lynchFilter, setLynchFilter] = useState('all');
    const [minPotential, setMinPotential] = useState<number | ''>('');
    const [minModels, setMinModels] = useState<number | ''>('');
    const [reliabilityFilter, setReliabilityFilter] = useState('all');
    const [ttmMethodFilter, setTtmMethodFilter] = useState('all');
    const [grahamFilter, setGrahamFilter] = useState('all');
    const [dcfFilter, setDcfFilter] = useState('all');
    const [psFilter, setPsFilter] = useState('all');
    const [pegFilter, setPegFilter] = useState('all');
    const [opportunityFilter, setOpportunityFilter] = useState<string[]>([]);

    // Sorting
    const [sortColumn, setSortColumn] = useState<SortColumn>('potentiel_pct');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

    const sectors = useMemo(() => {
        const s = new Set(data.map(d => d.secteur).filter(Boolean));
        return Array.from(s).sort();
    }, [data]);

    const countries = useMemo(() => {
        const c = new Set(data.map(d => d.pays).filter(Boolean));
        return Array.from(c).sort();
    }, [data]);

    // Signaux d'opportunité calculés une fois pour toutes les lignes
    const signalsMap = useMemo(() => {
        const map = new Map<string, OpportunitySignal[]>();
        data.forEach(item => map.set(item.symbole, computeOpportunitySignals(item)));
        return map;
    }, [data]);

    // Date de calcul affichée : celle du cache servi (la plus récente des lignes).
    const dateCalcul = useMemo(() => {
        const dates = data.map(d => d.cache_date).filter((d): d is string => !!d).sort();
        return dates.length ? dates[dates.length - 1].slice(0, 10) : null;
    }, [data]);

    // Métriques clés pour le bandeau KPI
    const stats = useMemo(() => {
        const total = data.length;
        const fastGrowers = data.filter(d => d.lynch_categorie === 'FAST_GROWER').length;
        const stalwarts = data.filter(d => d.lynch_categorie === 'STALWART').length;
        const undervalued = data.filter(d => d.recommendation === 'SOUS-ÉVALUÉE').length;
        const highFiab = data.filter(d => d.fiabilite === 'ÉLEVÉE' || d.fiabilite === 'ELEVEE').length;
        const opps = data.filter(d => (signalsMap.get(d.symbole) ?? []).length > 0).length;
        return { total, fastGrowers, stalwarts, undervalued, highFiab, opps };
    }, [data, signalsMap]);

    // Filtres actifs : une puce par filtre, chacune sachant se retirer seule.
    const activeFilters = useMemo(() => {
        const list: { key: string; label: string; clear: () => void }[] = [];
        const sens = (v: string) => (v === 'undervalued' ? 'décote ≥ 5 %' : 'prime ≥ 5 %');
        if (searchTerm !== '') list.push({ key: 'search', label: `Recherche : « ${searchTerm} »`, clear: () => setSearchTerm('') });
        if (lynchFilter !== 'all') list.push({ key: 'lynch', label: `Profil : ${LYNCH_LABELS[lynchFilter] ?? lynchFilter}`, clear: () => setLynchFilter('all') });
        if (sectorFilter !== 'all') list.push({ key: 'sector', label: `Secteur : ${sectorFilter}`, clear: () => setSectorFilter('all') });
        if (countryFilter !== 'all') list.push({ key: 'country', label: `Pays : ${countryFilter}`, clear: () => setCountryFilter('all') });
        if (recommendationFilter !== 'all') list.push({ key: 'statut', label: `Statut : ${STATUT_LABELS[recommendationFilter] ?? recommendationFilter}`, clear: () => setRecommendationFilter('all') });
        if (minPotential !== '') list.push({ key: 'potentiel', label: `Écart ≥ ${minPotential} %`, clear: () => setMinPotential('') });
        if (pegFilter !== 'all') list.push({ key: 'peg', label: `PEG : ${PEG_LABELS[pegFilter] ?? pegFilter}`, clear: () => setPegFilter('all') });
        if (grahamFilter !== 'all') list.push({ key: 'graham', label: `Graham : ${sens(grahamFilter)}`, clear: () => setGrahamFilter('all') });
        if (dcfFilter !== 'all') list.push({ key: 'dcf', label: `DCF : ${sens(dcfFilter)}`, clear: () => setDcfFilter('all') });
        if (psFilter !== 'all') list.push({ key: 'ps', label: `P/S : ${sens(psFilter)}`, clear: () => setPsFilter('all') });
        if (reliabilityFilter !== 'all') list.push({ key: 'fiabilite', label: `Fiabilité : ${reliabilityFilter.toLowerCase()}`, clear: () => setReliabilityFilter('all') });
        if (minModels !== '') list.push({ key: 'modeles', label: `Au moins ${minModels} modèles`, clear: () => setMinModels('') });
        if (ttmMethodFilter !== 'all') list.push({ key: 'ttm', label: `Annualisation : ${TTM_LABELS[ttmMethodFilter] ?? ttmMethodFilter}`, clear: () => setTtmMethodFilter('all') });
        opportunityFilter.forEach(id => {
            const def = ALL_SIGNAL_DEFS.find(s => s.id === id);
            list.push({
                key: `signal_${id}`,
                label: `Signal : ${def ? `${def.emoji} ${def.label}` : id}`,
                clear: () => setOpportunityFilter(prev => prev.filter(x => x !== id)),
            });
        });
        return list;
    }, [searchTerm, lynchFilter, sectorFilter, countryFilter, recommendationFilter, minPotential, pegFilter, grahamFilter, dcfFilter, psFilter, reliabilityFilter, minModels, ttmMethodFilter, opportunityFilter]);
    const activeFiltersCount = activeFilters.length;

    /** Une vue rapide est « allumée » quand les filtres qu'elle pose sont en place. */
    const isQuickViewActive = (id: string): boolean => {
        const seulSignal = (signal: string) => opportunityFilter.length === 1 && opportunityFilter[0] === signal;
        if (id === 'evrev_pe') return seulSignal('ev_rev_pe');
        if (id === 'triple_multiples') return seulSignal('triple_multiples');
        if (id === 'fast_grower') return lynchFilter === 'FAST_GROWER';
        if (id === 'undervalued') return recommendationFilter === 'SOUS-ÉVALUÉE' && minPotential === 15;
        if (id === 'complete_data') return minModels === 8 && reliabilityFilter === 'ÉLEVÉE';
        return false;
    };

    const applyPreset = (preset: string) => {
        setSearchTerm(''); setSectorFilter('all'); setCountryFilter('all');
        setRecommendationFilter('all'); setLynchFilter('all'); setMinPotential(''); setMinModels('');
        setReliabilityFilter('all'); setTtmMethodFilter('all');
        setGrahamFilter('all'); setDcfFilter('all'); setPsFilter('all'); setPegFilter('all'); setOpportunityFilter([]);
        if (preset === 'fast_grower') { setLynchFilter('FAST_GROWER'); }
        if (preset === 'stalwart') { setLynchFilter('STALWART'); }
        if (preset === 'undervalued') { setRecommendationFilter('SOUS-ÉVALUÉE'); setMinPotential(15); }
        if (preset === 'complete_data') { setMinModels(8); setReliabilityFilter('ÉLEVÉE'); }
        if (preset === 'opportunities') setOpportunityFilter(ALL_SIGNAL_DEFS.map(s => s.id));
        if (preset === 'triple_multiples') setOpportunityFilter(['triple_multiples']);
        if (preset === 'triple_conviction') setOpportunityFilter(['triple_conviction']);
        if (preset === 'evrev_pe') setOpportunityFilter(['ev_rev_pe']);
    };

    const filteredData = useMemo(() => {
        let result = data;
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            result = result.filter(d => d.symbole.toLowerCase().includes(term) || (d.nom_societe || '').toLowerCase().includes(term));
        }
        if (sectorFilter !== 'all') result = result.filter(d => d.secteur === sectorFilter);
        if (countryFilter !== 'all') result = result.filter(d => d.pays === countryFilter);
        if (recommendationFilter !== 'all') result = result.filter(d => d.recommendation === recommendationFilter);
        if (lynchFilter !== 'all') {
            result = result.filter(d => (d.lynch_categorie || '').trim().toUpperCase() === lynchFilter.trim().toUpperCase());
        }
        if (minPotential !== '') result = result.filter(d => (d.potentiel_pct ?? -999) >= minPotential);
        if (minModels !== '') result = result.filter(d => (d.nb_modeles ?? 0) >= minModels);
        if (reliabilityFilter !== 'all') result = result.filter(d => d.fiabilite === reliabilityFilter);
        if (ttmMethodFilter !== 'all') result = result.filter(d => composantesTtm(d.ttm_method).includes(ttmMethodFilter));
        // Filtres par modèle : sous-évalué = prix du modèle ≥ +5 % au-dessus du cours, sur-évalué = ≤ −5 %.
        const modelFilters: [string, ModelPriceColumn][] = [[grahamFilter, 'graham_number'], [dcfFilter, 'dcf'], [psFilter, 'ps']];
        modelFilters.forEach(([filter, column]) => {
            if (filter === 'all') return;
            result = result.filter(d => {
                const pot = ecartPct(d[column], d.cours_actuel);
                if (pot === null) return false;
                return filter === 'undervalued' ? pot >= 5 : pot <= -5;
            });
        });
        // PEG (Lynch) : score informatif, pas un prix — filtre sur le score lui-même,
        // pas sur un écart au cours actuel (cf. valuation.py calculate_peg_model).
        if (pegFilter !== 'all') result = result.filter(d => {
            if (d.peg_score == null) return false;
            if (pegFilter === 'very_attractive') return d.peg_score >= 2;
            if (pegFilter === 'good') return d.peg_score >= 1.5 && d.peg_score < 2;
            return d.peg_score < 1; // 'expensive'
        });
        // Filtre signaux d'opportunité : l'action doit avoir AU MOINS un des signaux cochés
        if (opportunityFilter.length > 0) {
            result = result.filter(d => {
                const itemSignals = signalsMap.get(d.symbole) ?? [];
                return opportunityFilter.some(filterId =>
                    itemSignals.some(s => s.id === filterId || s.id.startsWith(filterId === 'palier_imminent' ? 'palier_imminent' : filterId))
                );
            });
        }

        return [...result].sort((a, b) => {
            let aVal: number, bVal: number;
            const modelColumn = MODEL_PRICE_COLUMNS.find(c => c === sortColumn);
            if (modelColumn) {
                aVal = ecartPct(a[modelColumn], a.cours_actuel) ?? -999;
                bVal = ecartPct(b[modelColumn], b.cours_actuel) ?? -999;
            } else if (sortColumn === 'position_52s') {
                // Position du cours dans sa fourchette 52 semaines (0 % = au plus bas, 100 % = au plus haut)
                const position = (d: ValuationItem) =>
                    d.cours_actuel && d.prix_min_1an && d.prix_max_1an && d.prix_max_1an > d.prix_min_1an
                        ? ((d.cours_actuel - d.prix_min_1an) / (d.prix_max_1an - d.prix_min_1an)) * 100
                        : -999;
                aVal = position(a);
                bVal = position(b);
            } else if (sortColumn === 'fiabilite') {
                // Fiabilité d'abord, nombre de modèles pour départager
                const qualite = (d: ValuationItem) => (FIABILITE_RANK[d.fiabilite] ?? 0) * 100 + (d.nb_modeles ?? 0);
                aVal = qualite(a);
                bVal = qualite(b);
            } else {
                const av = a[sortColumn];
                const bv = b[sortColumn];
                if (typeof av === 'string' && typeof bv === 'string') {
                    return sortDirection === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
                }
                aVal = (av as number) ?? -999;
                bVal = (bv as number) ?? -999;
            }
            return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
        });
    }, [data, searchTerm, sectorFilter, countryFilter, recommendationFilter, lynchFilter, minPotential, minModels, reliabilityFilter, ttmMethodFilter, grahamFilter, dcfFilter, psFilter, pegFilter, opportunityFilter, signalsMap, sortColumn, sortDirection]);

    const handleSort = useCallback((column: SortColumn) => {
        if (sortColumn === column) setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortColumn(column); setSortDirection('desc'); }
    }, [sortColumn]);

    const formatPrice = (price: number | null) => {
        if (price === null) return '—';
        return price.toLocaleString('fr-FR') + ' F';
    };
    const formatPotential = (pct: number | null) => {
        if (pct === null) return '—';
        return `${pct > 0 ? '+' : ''}${pct.toFixed(1)}%`;
    };
    const getRecommendationStyle = (rec: string) => {
        if (rec === 'SOUS-ÉVALUÉE') return { bg: '#dcfce7', color: '#16a34a' };
        if (rec === 'SUR-ÉVALUÉE') return { bg: '#fee2e2', color: '#dc2626' };
        return { bg: '#fef3c7', color: '#d97706' };
    };
    /** Nombre de modèles calculés : celui de la synthèse backend (nb_modeles_valides), RIM compris. */
    const countValidModels = (item: ValuationItem) => item.nb_modeles ?? 0;
    const escapeCSV = (val: string) => {
        if (/[;"\n]/.test(val)) return `"${val.replace(/"/g, '""')}"`;
        return val;
    };
    /* Excel en français : séparateur « ; », virgule décimale, BOM UTF-8 pour les accents. */
    const nombreCSV = (v: number | null | undefined, decimales = 2): string =>
        v == null || !Number.isFinite(v) ? '' : String(Math.round(v * 10 ** decimales) / 10 ** decimales).replace('.', ',');

    const exportCSV = () => {
        const headers = ['Symbole', 'Société', 'Secteur', 'Pays', 'Cours', 'Plus bas 1 an', 'Plus haut 1 an', 'Modèles calculés', 'Fiabilité',
            'Graham Number', 'DCF', 'P/E', 'EV/Revenue', 'P/S', 'Score PEG', 'Prix moyen', 'Écart prix moyen %', 'Prix pondéré', 'Écart prix pondéré %', 'Statut', 'Date de calcul'];
        const rows = filteredData.map(d => {
            const potMoyen = (d.prix_moyen && d.cours_actuel) ? ((d.prix_moyen - d.cours_actuel) / d.cours_actuel * 100) : null;
            return [
                escapeCSV(d.symbole), escapeCSV(d.nom_societe || ''), escapeCSV(d.secteur || ''), escapeCSV(d.pays || ''),
                nombreCSV(d.cours_actuel), nombreCSV(d.prix_min_1an), nombreCSV(d.prix_max_1an),
                String(countValidModels(d)), escapeCSV(d.fiabilite || ''),
                nombreCSV(d.graham_number, 0), nombreCSV(d.dcf, 0), nombreCSV(d.pe, 0), nombreCSV(d.ev_revenue, 0), nombreCSV(d.ps, 0),
                nombreCSV(d.peg_score), nombreCSV(d.prix_moyen, 0), nombreCSV(potMoyen, 1),
                nombreCSV(d.prix_pondere, 0), nombreCSV(d.potentiel_pct, 1),
                escapeCSV(statutLabel(d.recommendation)), d.cache_date || '',
            ];
        });
        const csvContent = '\uFEFF' + [headers.join(';'), ...rows.map(r => r.join(';'))].join('\r\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `valorisation_${dateCalcul ?? new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        URL.revokeObjectURL(link.href);
    };

    const SortableHeader = ({ column, label, hint }: { column: SortColumn; label: string; hint?: string }) => (
        <th
            onClick={() => handleSort(column)}
            title={hint}
            style={{
                cursor: 'pointer', whiteSpace: 'nowrap', padding: '12px 10px',
                background: sortColumn === column ? 'rgba(99,102,241,0.12)' : '#f8fafc',
                borderBottom: `2px solid ${sortColumn === column ? '#6366f1' : '#e5e7eb'}`,
                fontSize: '11px', fontWeight: 700, letterSpacing: '0.03em',
                textAlign: column === 'symbole' ? 'left' : 'center',
                color: sortColumn === column ? '#6366f1' : '#374151',
                transition: 'all 0.15s ease',
                userSelect: 'none',
            }}
        >
            {label} {sortColumn === column && (sortDirection === 'asc' ? '↑' : '↓')}
        </th>
    );

    return (
        <SiteLayout>
            <style>{`
                @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
                @keyframes slideUp { from { opacity: 0; transform: translateY(24px) } to { opacity: 1; transform: translateY(0) } }
                .vp-row { transition: background 0.15s ease, transform 0.1s ease; cursor: pointer; }
                .vp-row:hover { background: rgba(99,102,241,0.04) !important; }
                .vp-btn-filter { transition: all 0.2s ease; }
                .vp-btn-filter:hover { opacity: 0.85; transform: translateY(-1px); }
                /* Mobile : cartes de synthèse en bandeau défilant au lieu d'une colonne de six */
                .vp-kpi--mobile { display: flex !important; overflow-x: auto; scrollbar-width: none; gap: 10px !important; margin: 0 -12px 14px !important; padding: 0 12px 4px; }
                .vp-kpi--mobile::-webkit-scrollbar { display: none; }
                .vp-kpi--mobile > div { flex: 0 0 172px; padding: 12px !important; gap: 10px !important; }
                .vp-kpi--mobile > div > div:first-child { width: 34px !important; height: 34px !important; }
            `}</style>

            <Header icon="filter_alt" title="🔍 Screener Valorisation" />

            <div style={{ padding: isMobile ? '12px' : '20px' }}>
                {/* ── Hero KPI Cards Banner ── */}
                <div className={isMobile ? 'vp-kpi--mobile' : undefined} style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
                    gap: '14px',
                    marginBottom: '20px',
                }}>
                    {/* Card 1: Total Cote */}
                    <div
                        onClick={() => applyPreset('reset')}
                        style={{
                            background: 'var(--bg-box, #ffffff)',
                            border: '1px solid var(--border-color, #e5e7eb)',
                            borderRadius: '12px',
                            padding: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                        }}
                    >
                        <div style={{
                            width: '42px', height: '42px', borderRadius: '10px',
                            background: 'rgba(59, 130, 246, 0.1)', color: '#2563eb',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                        }}>
                            <i className="material-icons" style={{ fontSize: '22px' }}>analytics</i>
                        </div>
                        <div>
                            <div style={{ fontSize: '11px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>
                                Cote BRVM
                            </div>
                            <div style={{ fontSize: '18px', fontWeight: 800, color: '#111827', marginTop: '2px' }}>
                                {stats.total}
                            </div>
                            <div style={{ fontSize: '10px', color: '#9ca3af' }}>Valeurs analysées</div>
                        </div>
                    </div>

                    {/* Card 2: Forte Croissance (Lynch) */}
                    <div
                        onClick={() => applyPreset('fast_grower')}
                        style={{
                            background: 'var(--bg-box, #ffffff)',
                            border: `2px solid ${lynchFilter === 'FAST_GROWER' ? '#7c3aed' : 'var(--border-color, #e5e7eb)'}`,
                            borderRadius: '12px',
                            padding: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                            boxShadow: lynchFilter === 'FAST_GROWER' ? '0 0 0 2px rgba(124, 58, 237, 0.2)' : '0 1px 4px rgba(0,0,0,0.05)',
                        }}
                    >
                        <div style={{
                            width: '42px', height: '42px', borderRadius: '10px',
                            background: 'rgba(124, 58, 237, 0.1)', color: '#7c3aed',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                        }}>
                            <span style={{ fontSize: '20px' }}>🚀</span>
                        </div>
                        <div>
                            <div style={{ fontSize: '11px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>
                                Forte Croissance
                            </div>
                            <div style={{ fontSize: '18px', fontWeight: 800, color: '#7c3aed', marginTop: '2px' }}>
                                {stats.fastGrowers}
                            </div>
                            <div style={{ fontSize: '10px', color: '#7c3aed', fontWeight: 600 }}>Profil Peter Lynch</div>
                        </div>
                    </div>

                    {/* Card 3: Piliers (Stalwarts) */}
                    <div
                        onClick={() => applyPreset('stalwart')}
                        style={{
                            background: 'var(--bg-box, #ffffff)',
                            border: `2px solid ${lynchFilter === 'STALWART' ? '#0284c7' : 'var(--border-color, #e5e7eb)'}`,
                            borderRadius: '12px',
                            padding: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                            boxShadow: lynchFilter === 'STALWART' ? '0 0 0 2px rgba(2, 132, 199, 0.2)' : '0 1px 4px rgba(0,0,0,0.05)',
                        }}
                    >
                        <div style={{
                            width: '42px', height: '42px', borderRadius: '10px',
                            background: 'rgba(2, 132, 199, 0.1)', color: '#0284c7',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                        }}>
                            <span style={{ fontSize: '20px' }}>🏛️</span>
                        </div>
                        <div>
                            <div style={{ fontSize: '11px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>
                                Piliers (Stalwarts)
                            </div>
                            <div style={{ fontSize: '18px', fontWeight: 800, color: '#0284c7', marginTop: '2px' }}>
                                {stats.stalwarts}
                            </div>
                            <div style={{ fontSize: '10px', color: '#0284c7', fontWeight: 600 }}>Résilience marché</div>
                        </div>
                    </div>

                    {/* Card 4: Sous-évaluées */}
                    <div
                        onClick={() => applyPreset('undervalued')}
                        style={{
                            background: 'var(--bg-box, #ffffff)',
                            border: `2px solid ${recommendationFilter === 'SOUS-ÉVALUÉE' ? '#16a34a' : 'var(--border-color, #e5e7eb)'}`,
                            borderRadius: '12px',
                            padding: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                            boxShadow: recommendationFilter === 'SOUS-ÉVALUÉE' ? '0 0 0 2px rgba(22, 163, 74, 0.2)' : '0 1px 4px rgba(0,0,0,0.05)',
                        }}
                    >
                        <div style={{
                            width: '42px', height: '42px', borderRadius: '10px',
                            background: 'rgba(22, 163, 74, 0.1)', color: '#16a34a',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                        }}>
                            <i className="material-icons" style={{ fontSize: '22px' }}>trending_up</i>
                        </div>
                        <div>
                            <div style={{ fontSize: '11px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>
                                Décote &gt; 20 %
                            </div>
                            <div style={{ fontSize: '18px', fontWeight: 800, color: '#16a34a', marginTop: '2px' }}>
                                {stats.undervalued}
                            </div>
                            <div style={{ fontSize: '10px', color: '#16a34a', fontWeight: 600 }}>Prix pondéré &gt; cours + 20 %</div>
                        </div>
                    </div>

                    {/* Card 5: Signaux Opportunité */}
                    <div
                        onClick={() => applyPreset('opportunities')}
                        style={{
                            background: 'var(--bg-box, #ffffff)',
                            border: `2px solid ${opportunityFilter.length > 0 ? '#ea580c' : 'var(--border-color, #e5e7eb)'}`,
                            borderRadius: '12px',
                            padding: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                            boxShadow: opportunityFilter.length > 0 ? '0 0 0 2px rgba(234, 88, 12, 0.2)' : '0 1px 4px rgba(0,0,0,0.05)',
                        }}
                    >
                        <div style={{
                            width: '42px', height: '42px', borderRadius: '10px',
                            background: 'rgba(234, 88, 12, 0.1)', color: '#ea580c',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                        }}>
                            <span style={{ fontSize: '20px' }}>🔥</span>
                        </div>
                        <div>
                            <div style={{ fontSize: '11px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>
                                Signaux de convergence
                            </div>
                            <div style={{ fontSize: '18px', fontWeight: 800, color: '#ea580c', marginTop: '2px' }}>
                                {stats.opps}
                            </div>
                            <div style={{ fontSize: '10px', color: '#ea580c', fontWeight: 600 }}>Au moins un signal</div>
                        </div>
                    </div>

                    {/* Card 6: Haute Fiabilité */}
                    <div
                        onClick={() => { applyPreset('reset'); setReliabilityFilter('ÉLEVÉE'); }}
                        style={{
                            background: 'var(--bg-box, #ffffff)',
                            border: `2px solid ${reliabilityFilter === 'ÉLEVÉE' ? '#0f766e' : 'var(--border-color, #e5e7eb)'}`,
                            borderRadius: '12px',
                            padding: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                            boxShadow: reliabilityFilter === 'ÉLEVÉE' ? '0 0 0 2px rgba(15, 118, 110, 0.2)' : '0 1px 4px rgba(0,0,0,0.05)',
                        }}
                    >
                        <div style={{
                            width: '42px', height: '42px', borderRadius: '10px',
                            background: 'rgba(15, 118, 110, 0.1)', color: '#0f766e',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                        }}>
                            <span style={{ fontSize: '20px' }}>💎</span>
                        </div>
                        <div>
                            <div style={{ fontSize: '11px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>
                                Fiabilité élevée
                            </div>
                            <div style={{ fontSize: '18px', fontWeight: 800, color: '#0f766e', marginTop: '2px' }}>
                                {stats.highFiab}
                            </div>
                            <div style={{ fontSize: '10px', color: '#0f766e', fontWeight: 600 }}>≥ 10 modèles cohérents</div>
                        </div>
                    </div>
                </div>

                <Box>
                    {/* ── Barre d'outils ── */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: 'var(--text-primary, #111827)' }}>
                                📊 Tableau Multicritères
                            </h2>
                            <span style={{
                                background: 'linear-gradient(135deg, #7c3aed, #6366f1)',
                                color: '#fff', padding: '3px 10px', borderRadius: '20px',
                                fontSize: '11px', fontWeight: 800, letterSpacing: '0.05em'
                            }}>PRO SCREENER</span>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                            {/* Recherche — toujours visible */}
                            <div style={{ position: 'relative', width: isMobile ? '100%' : undefined }}>
                                <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '13px', pointerEvents: 'none' }}>🔍</span>
                                <input
                                    type="text"
                                    aria-label="Rechercher une valeur"
                                    placeholder="Symbole ou société…"
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                    style={{ ...fieldStyle(searchTerm !== ''), width: isMobile ? '100%' : '210px', padding: '8px 30px 8px 32px', fontSize: isMobile ? '16px' : '12px' }}
                                />
                                {searchTerm && (
                                    <button
                                        type="button"
                                        aria-label="Effacer la recherche"
                                        onClick={() => setSearchTerm('')}
                                        style={{
                                            position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)',
                                            background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: '12px', padding: '4px',
                                        }}
                                    >✕</button>
                                )}
                            </div>

                            {/* Date */}
                            <label
                                htmlFor="valuation-date-select"
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '8px',
                                    backgroundColor: selectedDate !== 'latest' ? 'rgba(37, 99, 235, 0.08)' : 'var(--bg-box, #ffffff)',
                                    border: selectedDate !== 'latest' ? '1.5px solid #2563eb' : '1px solid #d1d5db',
                                    padding: '4px 10px', borderRadius: '8px', cursor: 'pointer',
                                }}
                            >
                                <span style={{ fontSize: '12px', fontWeight: 700, color: selectedDate !== 'latest' ? '#1d4ed8' : '#374151', whiteSpace: 'nowrap' }}>
                                    📅 Date
                                </span>
                                <select
                                    id="valuation-date-select"
                                    value={selectedDate}
                                    onChange={e => setSelectedDate(e.target.value)}
                                    style={{
                                        border: 'none', background: 'transparent', fontSize: '12px', fontWeight: 700,
                                        color: selectedDate !== 'latest' ? '#1d4ed8' : '#2563eb',
                                        cursor: 'pointer', outline: 'none', padding: '4px 2px',
                                    }}
                                >
                                    <option value="latest" style={{ background: '#ffffff', color: '#111827' }}>
                                        Dernière date disponible
                                    </option>
                                    {availableDates.map(dateStr => {
                                        const formatted = new Date(dateStr + 'T00:00:00').toLocaleDateString('fr-FR', {
                                            day: 'numeric', month: 'short', year: 'numeric'
                                        });
                                        return (
                                            <option key={dateStr} value={dateStr} style={{ background: '#ffffff', color: '#111827' }}>
                                                {formatted}
                                            </option>
                                        );
                                    })}
                                </select>
                                {selectedDate !== 'latest' && (
                                    <button
                                        type="button"
                                        onClick={(e) => { e.preventDefault(); setSelectedDate('latest'); }}
                                        title="Revenir à la date la plus récente"
                                        style={{
                                            border: 'none', background: '#2563eb', color: '#ffffff', borderRadius: '4px',
                                            padding: '2px 6px', fontSize: '11px', fontWeight: 700, cursor: 'pointer',
                                        }}
                                    >
                                        ✕
                                    </button>
                                )}
                            </label>

                            {/* Ouverture du panneau de filtres */}
                            <button
                                className="vp-btn-filter"
                                onClick={() => setFiltersOpen(o => !o)}
                                aria-expanded={filtersOpen}
                                style={{
                                    ...toolbarBtnStyle,
                                    background: filtersOpen ? '#0f172a' : 'var(--bg-box, #ffffff)',
                                    color: filtersOpen ? '#ffffff' : '#374151',
                                    borderColor: filtersOpen ? '#0f172a' : activeFiltersCount > 0 ? '#7c3aed' : '#d1d5db',
                                }}
                            >
                                <i className="material-icons" style={{ fontSize: '16px' }}>tune</i>
                                Filtres
                                {activeFiltersCount > 0 && (
                                    <span style={{
                                        background: '#7c3aed', color: '#fff', fontSize: '10px', fontWeight: 800,
                                        borderRadius: '10px', minWidth: '18px', height: '18px', padding: '0 5px',
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    }}>{activeFiltersCount}</span>
                                )}
                                <span style={{ fontSize: '9px' }}>{filtersOpen ? '▲' : '▼'}</span>
                            </button>

                            <button className="vp-btn-filter" onClick={exportCSV} style={{ ...toolbarBtnStyle, background: 'var(--bg-box, #ffffff)', color: '#374151' }}>
                                <i className="material-icons" style={{ fontSize: '16px' }}>download</i>
                                Export CSV
                            </button>
                        </div>
                    </div>

                    {/* ── Vues rapides ── */}
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px',
                        flexWrap: isMobile ? 'nowrap' : 'wrap', overflowX: isMobile ? 'auto' : undefined,
                        scrollbarWidth: 'none', whiteSpace: 'nowrap',
                    }}>
                        <span style={{ ...sectionLabelStyle, flexShrink: 0 }}>Vues rapides</span>
                        {QUICK_VIEWS.map(view => {
                            const active = isQuickViewActive(view.id);
                            return (
                                <button
                                    key={view.id}
                                    className="vp-btn-filter"
                                    aria-pressed={active}
                                    onClick={() => applyPreset(active ? 'reset' : view.id)}
                                    style={{
                                        background: active ? view.color : 'transparent',
                                        color: active ? '#ffffff' : view.color,
                                        border: `1.5px solid ${view.color}`,
                                        padding: '5px 13px', borderRadius: '20px',
                                        cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                                    }}
                                >
                                    {view.label}
                                </button>
                            );
                        })}
                    </div>

                    {/* ── Panneau de filtres ── */}
                    {filtersOpen && (
                        <div style={{
                            display: 'flex', flexDirection: 'column', gap: '18px',
                            marginBottom: '14px', padding: '18px',
                            background: 'rgba(248,250,252,0.9)',
                            borderRadius: '12px', border: '1px solid #e5e7eb',
                            animation: 'slideUp 0.2s ease'
                        }}>
                            <FilterSection title="Société">
                                <FilterField label="Profil Lynch" active={lynchFilter !== 'all'}>
                                    <select value={lynchFilter} onChange={e => setLynchFilter(e.target.value)} style={fieldStyle(lynchFilter !== 'all')}>
                                        <option value="all">Tous les profils</option>
                                        {Object.entries(LYNCH_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                                    </select>
                                </FilterField>
                                <FilterField label="Secteur" active={sectorFilter !== 'all'}>
                                    <select value={sectorFilter} onChange={e => setSectorFilter(e.target.value)} style={fieldStyle(sectorFilter !== 'all')}>
                                        <option value="all">Tous</option>
                                        {sectors.map(s => <option key={s} value={s!}>{s}</option>)}
                                    </select>
                                </FilterField>
                                <FilterField label="Pays" active={countryFilter !== 'all'}>
                                    <select value={countryFilter} onChange={e => setCountryFilter(e.target.value)} style={fieldStyle(countryFilter !== 'all')}>
                                        <option value="all">Tous</option>
                                        {countries.map(c => <option key={c} value={c!}>{c}</option>)}
                                    </select>
                                </FilterField>
                            </FilterSection>

                            <FilterSection title="Valorisation" hint="Graham, DCF et P/S : décote si le prix du modèle dépasse le cours d'au moins 5 %, prime s'il lui est inférieur d'au moins 5 %.">
                                <FilterField label="Statut" active={recommendationFilter !== 'all'}>
                                    <select value={recommendationFilter} onChange={e => setRecommendationFilter(e.target.value)} style={fieldStyle(recommendationFilter !== 'all')}>
                                        <option value="all">Tous</option>
                                        {Object.entries(STATUT_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                                    </select>
                                </FilterField>
                                <FilterField label="Écart min. au prix pondéré (%)" active={minPotential !== ''}>
                                    <input type="number" placeholder="Ex : 10" value={minPotential}
                                        onChange={e => setMinPotential(e.target.value ? Number(e.target.value) : '')} style={fieldStyle(minPotential !== '')} />
                                </FilterField>
                                <FilterField label="PEG (Lynch)" active={pegFilter !== 'all'}>
                                    <select value={pegFilter} onChange={e => setPegFilter(e.target.value)} style={fieldStyle(pegFilter !== 'all')}>
                                        <option value="all">Tous</option>
                                        {Object.entries(PEG_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                                    </select>
                                </FilterField>
                                <FilterField label="Graham" active={grahamFilter !== 'all'}>
                                    <SegmentedFilter label="Graham" value={grahamFilter} onChange={setGrahamFilter} />
                                </FilterField>
                                <FilterField label="DCF" active={dcfFilter !== 'all'}>
                                    <SegmentedFilter label="DCF" value={dcfFilter} onChange={setDcfFilter} />
                                </FilterField>
                                <FilterField label="P/S" active={psFilter !== 'all'}>
                                    <SegmentedFilter label="P/S" value={psFilter} onChange={setPsFilter} />
                                </FilterField>
                            </FilterSection>

                            <FilterSection title="Qualité des données">
                                <FilterField label="Fiabilité" active={reliabilityFilter !== 'all'}>
                                    <select value={reliabilityFilter} onChange={e => setReliabilityFilter(e.target.value)} style={fieldStyle(reliabilityFilter !== 'all')}>
                                        <option value="all">Toutes</option>
                                        <option value="ÉLEVÉE">Élevée</option>
                                        <option value="MOYENNE">Moyenne</option>
                                        <option value="SPÉCULATIVE">Spéculative</option>
                                        <option value="FAIBLE">Faible</option>
                                    </select>
                                </FilterField>
                                <FilterField label="Modèles calculés" active={minModels !== ''}>
                                    <select value={minModels} onChange={e => setMinModels(e.target.value ? Number(e.target.value) : '')} style={fieldStyle(minModels !== '')}>
                                        <option value="">Tous</option>
                                        <option value="4">Au moins 4</option>
                                        <option value="6">Au moins 6</option>
                                        <option value="8">Au moins 8</option>
                                        <option value="10">Au moins 10</option>
                                    </select>
                                </FilterField>
                                <FilterField label="Annualisation des comptes" active={ttmMethodFilter !== 'all'}>
                                    <select value={ttmMethodFilter} onChange={e => setTtmMethodFilter(e.target.value)} style={fieldStyle(ttmMethodFilter !== 'all')}>
                                        <option value="all">Toutes</option>
                                        {Object.entries(TTM_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                                    </select>
                                </FilterField>
                            </FilterSection>

                            <FilterSection title="Signaux de convergence" hint="Une valeur est retenue si elle porte au moins un des signaux cochés. Signaux descriptifs : ni recommandation ni objectif de cours." minWidth={240}>
                                {ALL_SIGNAL_DEFS.map(def => {
                                    const active = opportunityFilter.includes(def.id);
                                    return (
                                        <button
                                            key={def.id}
                                            aria-pressed={active}
                                            onClick={() => setOpportunityFilter(prev =>
                                                active ? prev.filter(id => id !== def.id) : [...prev, def.id]
                                            )}
                                            style={{
                                                display: 'flex', alignItems: 'flex-start', gap: '10px',
                                                padding: '10px 12px', borderRadius: '10px', cursor: 'pointer',
                                                border: `1.5px solid ${active ? def.color : '#e5e7eb'}`,
                                                background: active ? def.bg : 'var(--bg-box, #fff)',
                                                transition: 'all 0.15s ease', textAlign: 'left',
                                            }}
                                        >
                                            <span style={{ fontSize: '18px', lineHeight: 1, flexShrink: 0 }}>{def.emoji}</span>
                                            <span style={{ flex: 1 }}>
                                                <span style={{ fontSize: '12px', fontWeight: 700, color: active ? def.color : '#374151', display: 'block' }}>
                                                    {def.label}
                                                </span>
                                                <span style={{ fontSize: '11px', color: '#6b7280', lineHeight: '1.35', display: 'block', marginTop: '3px' }}>
                                                    {def.desc}
                                                </span>
                                            </span>
                                            <span style={{
                                                width: '16px', height: '16px', borderRadius: '4px', flexShrink: 0, marginTop: '1px',
                                                border: `1.5px solid ${active ? def.color : '#cbd5e1'}`,
                                                background: active ? def.color : 'transparent',
                                                color: '#fff', fontSize: '11px', fontWeight: 800, lineHeight: '13px', textAlign: 'center',
                                            }}>{active ? '✓' : ''}</span>
                                        </button>
                                    );
                                })}
                            </FilterSection>
                        </div>
                    )}

                    {/* ── Filtres actifs ── */}
                    {activeFilters.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '12px' }}>
                            {activeFilters.map(f => (
                                <button
                                    key={f.key}
                                    onClick={f.clear}
                                    title="Retirer ce filtre"
                                    style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                                        background: '#ede9fe', color: '#6d28d9', border: '1px solid #ddd6fe',
                                        padding: '4px 10px', borderRadius: '20px',
                                        fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                                    }}
                                >
                                    {f.label}
                                    <span aria-hidden="true" style={{ fontSize: '10px' }}>✕</span>
                                </button>
                            ))}
                            <button onClick={() => applyPreset('reset')} style={clearAllBtnStyle}>Tout effacer</button>
                        </div>
                    )}

                    {/* ── Stats bar ── */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '14px', fontSize: '13px', color: '#6b7280', flexWrap: 'wrap' }}>
                        <span><strong style={{ color: '#111827' }}>{filteredData.length}</strong> actions affichées sur {data.length}</span>
                        <span style={{ color: '#9ca3af', fontSize: '12px' }}>{isMobile ? 'Touchez une carte' : 'Cliquez sur une ligne'} pour le détail des {NB_MODELES} modèles</span>
                        {dateCalcul && (
                            <span style={{ marginLeft: 'auto', fontSize: '12px' }}>
                                Calculé le {new Date(dateCalcul + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                            </span>
                        )}
                    </div>

                    {/* ── Table ── */}
                    {loading ? (
                        <Loader message="Calcul des valorisations en cours..." />
                    ) : error ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#dc2626', background: '#fef2f2', borderRadius: '12px' }}>{error}</div>
                    ) : isMobile ? (
                        <ListeMobile
                            items={filteredData}
                            signalsMap={signalsMap}
                            sortColumn={sortColumn}
                            sortDirection={sortDirection}
                            onSort={handleSort}
                            onSelect={setSelectedItem}
                            onReset={activeFiltersCount > 0 ? () => applyPreset('reset') : undefined}
                        />
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr>
                                        <SortableHeader column="symbole" label="Valeur / Profil" />
                                        <SortableHeader column="cours_actuel" label="Cours" />
                                        <SortableHeader column="position_52s" label="Fourchette 52 sem." hint="Plus bas et plus haut sur 1 an. ● cours actuel, ◆ prix pondéré. Tri : position du cours dans la fourchette." />
                                        <SortableHeader column="graham_number" label="Graham" hint="Tri sur l'écart au cours" />
                                        <SortableHeader column="dcf" label="DCF" hint="Tri sur l'écart au cours" />
                                        <SortableHeader column="ev_revenue" label="EV/Rev" hint="Tri sur l'écart au cours" />
                                        <SortableHeader column="ps" label="P/S" hint="Prix selon le multiple cours / chiffre d'affaires. Tri sur l'écart au cours." />
                                        <SortableHeader column="peg_score" label="PEG" hint="Score PEG (Lynch) — informatif, ce n'est pas un prix" />
                                        <SortableHeader column="prix_moyen" label="Prix Moyen" hint="Tri sur l'écart au cours" />
                                        <SortableHeader column="potentiel_pct" label="Prix Pondéré" hint="Tri sur le potentiel" />
                                        <SortableHeader column="fiabilite" label="Qualité" hint="Fiabilité, nombre de modèles calculés et qualité des données TTM. Tri : fiabilité puis nombre de modèles." />
                                        <SortableHeader column="recommendation" label="Écart & signaux" hint="Écart entre le prix pondéré et le cours (seuil ±20 %), puis signaux de convergence" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredData.length === 0 && (
                                        <tr>
                                            <td colSpan={12} style={{ padding: '40px 20px', textAlign: 'center', color: '#6b7280', fontSize: '13px' }}>
                                                <div style={{ fontWeight: 600, color: '#374151', marginBottom: '6px' }}>Aucune valeur ne correspond à ces filtres.</div>
                                                {activeFiltersCount > 0 && (
                                                    <button onClick={() => applyPreset('reset')} style={clearAllBtnStyle}>Tout effacer</button>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                    {filteredData.map((item, idx) => {
                                        const recoStyle = getRecommendationStyle(item.recommendation);
                                        const validModels = countValidModels(item);
                                        const fiab = item.fiabilite || '';
                                        const fiabStyle = fiab === 'ÉLEVÉE' || fiab === 'ELEVEE'
                                            ? { bg: '#dcfce7', color: '#16a34a' }
                                            : fiab === 'MOYENNE'
                                            ? { bg: '#fef3c7', color: '#d97706' }
                                            : fiab === 'SPÉCULATIVE' || fiab === 'SPECULATIVE'
                                            ? { bg: '#ffedd5', color: '#f97316' }
                                            : { bg: '#fee2e2', color: '#dc2626' };

                                        return (
                                            <tr
                                                key={item.symbole}
                                                className="vp-row"
                                                onClick={() => setSelectedItem(item)}
                                                style={{ borderBottom: '1px solid #f1f5f9', background: idx % 2 === 0 ? 'transparent' : 'rgba(248,250,252,0.5)' }}
                                            >
                                                {/* Symbole & Profil Lynch */}
                                                <td style={{ padding: '12px 10px', fontWeight: 600, minWidth: '150px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                        <span style={{ fontSize: '14px', color: '#111827', fontWeight: 700 }}>{item.symbole}</span>
                                                        {item.lynch_categorie && (
                                                            <span
                                                                title={`Profil Peter Lynch : ${item.lynch_titre}`}
                                                                style={{
                                                                    fontSize: '10px',
                                                                    fontWeight: 700,
                                                                    padding: '2px 6px',
                                                                    borderRadius: '8px',
                                                                    background: item.lynch_categorie === 'FAST_GROWER' ? 'rgba(124, 58, 237, 0.12)' : 'rgba(100, 116, 139, 0.1)',
                                                                    color: item.lynch_categorie === 'FAST_GROWER' ? '#7c3aed' : '#475569',
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    gap: '3px',
                                                                }}
                                                            >
                                                                {item.lynch_emoji} {item.lynch_titre}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: 400, maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: '2px' }}>
                                                        {item.nom_societe}
                                                    </div>
                                                </td>
                                                {/* Cours actuel */}
                                                <td style={{ textAlign: 'center', padding: '12px 10px', fontWeight: 700, fontSize: '13px', color: '#1e40af' }}>
                                                    {formatPrice(item.cours_actuel)}
                                                </td>
                                                {/* Fourchette 52 semaines : plus bas, plus haut et jauge */}
                                                <td style={{ padding: '12px 10px', minWidth: '150px' }}>
                                                    {item.prix_min_1an && item.prix_max_1an ? (
                                                        <>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', fontSize: '11px', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                                                                <span style={{ color: '#dc2626' }} title="Plus bas sur 1 an">{item.prix_min_1an.toLocaleString('fr-FR')}</span>
                                                                <span style={{ color: '#16a34a' }} title="Plus haut sur 1 an">{item.prix_max_1an.toLocaleString('fr-FR')}</span>
                                                            </div>
                                                            <PriceGauge
                                                                min={item.prix_min_1an}
                                                                max={item.prix_max_1an}
                                                                current={item.cours_actuel}
                                                                target={item.prix_pondere}
                                                            />
                                                        </>
                                                    ) : <div style={{ textAlign: 'center', color: '#cbd5e1' }}>—</div>}
                                                </td>
                                                <ModelPriceCell price={item.graham_number} cours={item.cours_actuel} />
                                                <ModelPriceCell price={item.dcf} cours={item.cours_actuel} />
                                                {/* EV/Revenue — multiple de ventes (moteur de la stratégie value BRVM) */}
                                                <ModelPriceCell price={item.ev_revenue} cours={item.cours_actuel} />
                                                <ModelPriceCell price={item.ps} cours={item.cours_actuel} />
                                                {/* PEG (Lynch) — score informatif, pas un prix */}
                                                <td style={{ textAlign: 'center', padding: '12px 10px', fontSize: '12px' }}>
                                                    {item.peg_score != null ? (
                                                        <div style={{
                                                            fontWeight: 700,
                                                            color: item.peg_score >= 2 ? '#16a34a' : item.peg_score >= 1.5 ? '#2563eb' : item.peg_score >= 1 ? '#d97706' : '#dc2626',
                                                        }}>
                                                            {item.peg_score.toFixed(2)}
                                                        </div>
                                                    ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                                                </td>
                                                <ModelPriceCell price={item.prix_moyen} cours={item.cours_actuel} priceColor="#6366f1" emphasis />
                                                {/* Prix pondéré */}
                                                <td style={{ textAlign: 'center', padding: '12px 10px', fontSize: '12px', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                                                    <div style={{ fontWeight: 700, color: '#7c3aed', fontSize: '13px' }}>{formatPrice(item.prix_pondere)}</div>
                                                    <div style={{ fontSize: '11px', fontWeight: 700, color: (item.potentiel_pct ?? 0) > 0 ? '#16a34a' : '#dc2626' }}>
                                                        {formatPotential(item.potentiel_pct)}
                                                    </div>
                                                </td>
                                                {/* Qualité : fiabilité, modèles calculés, données TTM */}
                                                <td style={{ textAlign: 'center', padding: '12px 10px', whiteSpace: 'nowrap' }}>
                                                    <span style={{ background: fiabStyle.bg, color: fiabStyle.color, padding: '3px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 700 }}>
                                                        {fiab || '—'}
                                                    </span>
                                                    <div style={{ fontSize: '11px', marginTop: '5px', fontWeight: 600, color: validModels >= 8 ? '#16a34a' : validModels >= 4 ? '#d97706' : '#dc2626' }}>
                                                        {validModels}/{NB_MODELES} modèles
                                                    </div>
                                                    <div
                                                        style={{ fontSize: '10px', color: '#9ca3af', maxWidth: '150px', whiteSpace: 'normal', margin: '0 auto' }}
                                                        title={`Comptes annualisés : ${libelleTtm(item.ttm_method)}. Score de fiabilité de l'annualisation : ${item.ttm_reliability} %.`}
                                                    >
                                                        TTM <span style={{ fontWeight: 600, color: item.ttm_reliability >= 80 ? '#16a34a' : '#d97706' }}>{item.ttm_reliability}%</span>
                                                        {' · '}{libelleTtm(item.ttm_method, true)}
                                                    </div>
                                                </td>
                                                {/* Statut et signaux d'opportunité */}
                                                <td style={{ padding: '10px 10px', minWidth: '150px' }}>
                                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                                                        <span style={{
                                                            background: recoStyle.bg, color: recoStyle.color,
                                                            padding: '4px 10px', borderRadius: '20px',
                                                            fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap'
                                                        }}>
                                                            {statutLabel(item.recommendation)}
                                                        </span>
                                                        {(signalsMap.get(item.symbole) ?? []).map(sig => (
                                                            <span
                                                                key={sig.id}
                                                                title={sig.tooltip}
                                                                style={{
                                                                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                                                                    background: sig.bg, color: sig.color,
                                                                    padding: '3px 8px', borderRadius: '20px',
                                                                    fontSize: '11px', fontWeight: 700,
                                                                    whiteSpace: 'nowrap', cursor: 'help',
                                                                }}
                                                            >
                                                                {sig.emoji} {sig.label}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Box>
            </div>

            {/* ── Drill-Down Modal ── */}
            {selectedItem && (
                <DrillDownModal
                    item={selectedItem}
                    selectedDate={selectedDate}
                    onClose={() => setSelectedItem(null)}
                    compact={isMobile}
                />
            )}
        </SiteLayout>
    );
};

/* ─────────────────────────────────────────────
   Liste mobile : une carte par valeur (le tableau de 12 colonnes est illisible sous 768 px)
───────────────────────────────────────────── */
const TRIS_MOBILE: { value: SortColumn; label: string }[] = [
    { value: 'potentiel_pct', label: 'Écart au prix pondéré' },
    { value: 'prix_moyen', label: 'Écart au prix moyen' },
    { value: 'fiabilite', label: 'Fiabilité' },
    { value: 'position_52s', label: 'Position sur 52 semaines' },
    { value: 'graham_number', label: 'Écart Graham' },
    { value: 'dcf', label: 'Écart DCF' },
    { value: 'ev_revenue', label: 'Écart EV/Revenue' },
    { value: 'ps', label: 'Écart P/S' },
    { value: 'peg_score', label: 'Score PEG' },
    { value: 'cours_actuel', label: 'Cours' },
    { value: 'symbole', label: 'Symbole' },
];

const styleFiabilite = (fiab: string) =>
    fiab === 'ÉLEVÉE' || fiab === 'ELEVEE' ? { bg: '#dcfce7', color: '#16a34a' }
    : fiab === 'MOYENNE' ? { bg: '#fef3c7', color: '#d97706' }
    : fiab === 'SPÉCULATIVE' || fiab === 'SPECULATIVE' ? { bg: '#ffedd5', color: '#f97316' }
    : { bg: '#fee2e2', color: '#dc2626' };

const styleStatut = (rec: string) =>
    rec === 'SOUS-ÉVALUÉE' ? { bg: '#dcfce7', color: '#16a34a' }
    : rec === 'SUR-ÉVALUÉE' ? { bg: '#fee2e2', color: '#dc2626' }
    : { bg: '#fef3c7', color: '#d97706' };

const pctTexte = (pct: number | null) => (pct === null ? '—' : `${pct > 0 ? '+' : ''}${pct.toFixed(1)} %`);
const pctCouleur = (pct: number | null) => (pct === null ? '#94a3b8' : pct > 0 ? '#16a34a' : '#dc2626');

const ListeMobile: React.FC<{
    items: ValuationItem[];
    signalsMap: Map<string, OpportunitySignal[]>;
    sortColumn: SortColumn;
    sortDirection: SortDirection;
    onSort: (c: SortColumn) => void;
    onSelect: (item: ValuationItem) => void;
    onReset?: () => void;
}> = ({ items, signalsMap, sortColumn, sortDirection, onSort, onSelect, onReset }) => (
    <div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px' }}>
            <select
                aria-label="Trier par"
                value={sortColumn}
                onChange={e => onSort(e.target.value as SortColumn)}
                style={{ ...fieldStyle(false), flex: 1, fontSize: '14px', padding: '9px 10px', fontWeight: 600 }}
            >
                {TRIS_MOBILE.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <button
                type="button"
                onClick={() => onSort(sortColumn)}
                aria-label={sortDirection === 'desc' ? 'Tri décroissant, inverser' : 'Tri croissant, inverser'}
                style={{ ...toolbarBtnStyle, background: 'var(--bg-box, #fff)', color: '#374151', padding: '9px 12px', flexShrink: 0 }}
            >
                {sortDirection === 'desc' ? '↓ Décroissant' : '↑ Croissant'}
            </button>
        </div>

        {items.length === 0 && (
            <div style={{ padding: '32px 12px', textAlign: 'center', color: '#6b7280', fontSize: '13px' }}>
                <div style={{ fontWeight: 600, color: '#374151', marginBottom: '6px' }}>Aucune valeur ne correspond à ces filtres.</div>
                {onReset && <button onClick={onReset} style={clearAllBtnStyle}>Tout effacer</button>}
            </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {items.map(item => {
                const cours = item.cours_actuel;
                const fiab = styleFiabilite(item.fiabilite || '');
                const statut = styleStatut(item.recommendation);
                const signaux = signalsMap.get(item.symbole) ?? [];
                const modeles: { label: string; prix: number | null | undefined }[] = [
                    { label: 'Graham', prix: item.graham_number },
                    { label: 'DCF', prix: item.dcf },
                    { label: 'EV/Rev', prix: item.ev_revenue },
                    { label: 'P/S', prix: item.ps },
                ];
                return (
                    <button
                        key={item.symbole}
                        type="button"
                        onClick={() => onSelect(item)}
                        style={{
                            textAlign: 'left', width: '100%', cursor: 'pointer',
                            background: 'var(--bg-box, #ffffff)', border: '1px solid var(--border-color, #e5e7eb)',
                            borderRadius: '14px', padding: '14px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                            font: 'inherit', color: 'inherit',
                        }}
                    >
                        {/* Identité et statut */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'flex-start' }}>
                            <div style={{ minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                    <span style={{ fontSize: '16px', fontWeight: 800, color: '#111827' }}>{item.symbole}</span>
                                    {item.lynch_categorie && (
                                        <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 6px', borderRadius: '8px', background: 'rgba(100,116,139,0.1)', color: '#475569' }}>
                                            {item.lynch_emoji} {item.lynch_titre}
                                        </span>
                                    )}
                                </div>
                                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {item.nom_societe}
                                </div>
                            </div>
                            <span style={{ background: statut.bg, color: statut.color, padding: '4px 9px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0 }}>
                                {statutLabel(item.recommendation)}
                            </span>
                        </div>

                        {/* Cours, prix pondéré, prix moyen */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px', marginTop: '12px' }}>
                            {[
                                { label: 'Cours', valeur: cours, pct: null as number | null, couleur: '#1e40af' },
                                { label: 'Prix pondéré', valeur: item.prix_pondere, pct: item.potentiel_pct, couleur: '#7c3aed' },
                                { label: 'Prix moyen', valeur: item.prix_moyen, pct: ecartPct(item.prix_moyen, cours), couleur: '#6366f1' },
                            ].map(b => (
                                <div key={b.label} style={{ background: 'rgba(0,0,0,0.03)', borderRadius: '10px', padding: '8px' }}>
                                    <div style={{ fontSize: '9px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{b.label}</div>
                                    <div style={{ fontSize: '13px', fontWeight: 800, color: b.couleur, marginTop: '2px', fontVariantNumeric: 'tabular-nums' }}>
                                        {b.valeur != null ? `${b.valeur.toLocaleString('fr-FR')} F` : '—'}
                                    </div>
                                    {b.label !== 'Cours' && (
                                        <div style={{ fontSize: '11px', fontWeight: 700, color: pctCouleur(b.pct) }}>{pctTexte(b.pct)}</div>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* Fourchette 52 semaines */}
                        {item.prix_min_1an && item.prix_max_1an ? (
                            <div style={{ marginTop: '12px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#6b7280', fontVariantNumeric: 'tabular-nums' }}>
                                    <span>52 sem. : {item.prix_min_1an.toLocaleString('fr-FR')}</span>
                                    <span>{item.prix_max_1an.toLocaleString('fr-FR')}</span>
                                </div>
                                <PriceGauge min={item.prix_min_1an} max={item.prix_max_1an} current={cours} target={item.prix_pondere} />
                            </div>
                        ) : null}

                        {/* Écart de quatre modèles de référence */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '4px', marginTop: '14px' }}>
                            {modeles.map(m => {
                                const pct = ecartPct(m.prix, cours);
                                return (
                                    <div key={m.label} style={{ textAlign: 'center' }}>
                                        <div style={{ fontSize: '9px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>{m.label}</div>
                                        <div style={{ fontSize: '11px', fontWeight: 700, color: pctCouleur(pct) }}>{pctTexte(pct)}</div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Qualité */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginTop: '12px', fontSize: '11px', color: '#6b7280' }}>
                            <span style={{ background: fiab.bg, color: fiab.color, padding: '2px 8px', borderRadius: '6px', fontWeight: 700 }}>{item.fiabilite || '—'}</span>
                            <span style={{ fontWeight: 600 }}>{item.nb_modeles ?? 0}/{NB_MODELES} modèles</span>
                            <span>TTM {item.ttm_reliability}% · {libelleTtm(item.ttm_method, true)}</span>
                        </div>

                        {/* Signaux de convergence */}
                        {signaux.length > 0 && (
                            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '10px' }}>
                                {signaux.map(sig => (
                                    <span key={sig.id} style={{ background: sig.bg, color: sig.color, padding: '3px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: 700 }}>
                                        {sig.emoji} {sig.label}
                                    </span>
                                ))}
                            </div>
                        )}
                    </button>
                );
            })}
        </div>
    </div>
);

/* ─────────────────────────────────────────────
   Helpers
───────────────────────────────────────────── */
const sectionLabelStyle: React.CSSProperties = {
    fontSize: '11px', fontWeight: 700, color: '#94a3b8',
    textTransform: 'uppercase', letterSpacing: '0.06em',
};

/** Groupe de filtres titré ; `minWidth` règle la largeur minimale d'une case de la grille. */
const FilterSection: React.FC<{ title: string; hint?: string; minWidth?: number; children: React.ReactNode }> = ({ title, hint, minWidth = 170, children }) => (
    <div>
        <div style={{ ...sectionLabelStyle, marginBottom: '8px' }}>{title}</div>
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${minWidth}px, 1fr))`, gap: '10px' }}>
            {children}
        </div>
        {hint && <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '6px' }}>{hint}</div>}
    </div>
);

const FilterField: React.FC<{ label: string; active?: boolean; children: React.ReactNode }> = ({ label, active = false, children }) => (
    <div>
        <div style={{ fontSize: '11px', color: active ? '#7c3aed' : '#6b7280', marginBottom: '4px', fontWeight: 600 }}>{label}</div>
        {children}
    </div>
);

/** Filtre à trois états pour un modèle de prix (Graham, DCF, P/S). */
const SegmentedFilter: React.FC<{ label: string; value: string; onChange: (v: string) => void }> = ({ label, value, onChange }) => {
    const options = [
        { value: 'all', text: 'Tous', color: '#475569' },
        { value: 'undervalued', text: 'Décote', color: '#16a34a' },
        { value: 'overvalued', text: 'Prime', color: '#dc2626' },
    ];
    return (
        <div role="group" aria-label={label} style={{ display: 'flex', background: '#eef2f7', borderRadius: '8px', padding: '2px', gap: '2px', height: '33px', boxSizing: 'border-box' }}>
            {options.map(o => {
                const active = o.value === value;
                return (
                    <button
                        key={o.value}
                        type="button"
                        aria-pressed={active}
                        onClick={() => onChange(o.value)}
                        style={{
                            flex: 1, border: 'none', borderRadius: '6px', cursor: 'pointer',
                            fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap', padding: '0 4px',
                            background: active ? '#ffffff' : 'transparent',
                            color: active ? o.color : '#6b7280',
                            boxShadow: active ? '0 1px 2px rgba(0,0,0,0.12)' : 'none',
                            transition: 'all 0.15s ease',
                        }}
                    >
                        {o.text}
                    </button>
                );
            })}
        </div>
    );
};

/** Champ de saisie ; la bordure passe au violet quand le filtre est actif. */
const fieldStyle = (active: boolean): React.CSSProperties => ({
    width: '100%', padding: '8px 10px', borderRadius: '8px',
    border: `1px solid ${active ? '#7c3aed' : '#d1d5db'}`, fontSize: '12px',
    background: 'var(--bg-box, #fff)', boxSizing: 'border-box',
});

const toolbarBtnStyle: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    border: '1px solid #d1d5db', padding: '7px 12px', borderRadius: '8px',
    cursor: 'pointer', fontSize: '12px', fontWeight: 600,
};

const clearAllBtnStyle: React.CSSProperties = {
    background: 'none', border: 'none', color: '#6b7280', padding: '4px 6px',
    fontSize: '12px', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline',
};

export default SpecialVraiPrixScreen;
